import * as C from './constants.js';
export const TUNING_SECTIONS = [
    { id: 'economy', title: 'Economy and treasury', names: [
            'START_RESOURCES', 'INCOME', 'CONVERSION', 'DEFENCE_UPKEEP', 'INTERCEPT_BOUNTY',
        ] },
    { id: 'plan', title: 'Plan phase and production', names: [
            'BASE_ACTION_POINTS', 'STAFF_ACTION_POINTS', 'PRODUCTION_LINES', 'PRODUCTION_BATCH', 'DEFENCE_BATCH',
            'PRODUCTION_SURGE_CHANCE', 'PRODUCTION_SURGE_MULT',
            'START_ARSENAL', 'START_DEFENCES',
        ] },
    { id: 'rearm', title: 'Rearming', names: ['REARM_BASE', 'REARM_COST_PER_ROUND', 'REARM_AP'] },
    { id: 'weapons', title: 'Weapons and launch', names: [
            'WEAPON_SPECS', 'ICBM_PER_WAVE', 'BALLISTIC_PER_WAVE', 'AIRBASE_SLOTS', 'SILO_SLOTS', 'STOCKPILE_WAVES', 'BASE_WAVES', 'WAVE_DOCTRINE_BONUS',
            'MULTI_WAVE_COMMAND_FLOOR', 'DOGLEG_FUEL_PER_KM', 'DOGLEG_FUEL_PER_ROUND', 'DOGLEG_STANDOFF_FRACTION', 'DOGLEG_STANDOFF_MIN_KM',
        ] },
    { id: 'warheads', title: 'Warheads and special weapons', names: [
            'WARHEAD_SPECS', 'THERMOBARIC_RESIDENTIAL_CASUALTIES', 'CHEMICAL_ROUNDS', 'CHEMICAL_CEP_MULT',
            'CHEMICAL_DEFENCE_MULT',
        ] },
    { id: 'airDefence', title: 'Air defence', names: [
            'DEFENCE_SPECS', 'DEFENCE_AMMO', 'SHELTER_CASUALTY_REDUCTION', 'ACTIVE_POSTURE_PK_BONUS',
            'EW_STACK_FALLOFF', 'HOLD_BACK', 'MAX_DIVERT_CHANCE',
        ] },
    { id: 'doctrine', title: 'Doctrine', names: ['DOCTRINE_SPECS'] },
    { id: 'accuracy', title: 'Accuracy and damage', names: [
            'RESILIENCE_EXP', 'CAPABILITY_FLOOR', 'RANGE_CEP_PENALTY', 'EW_CEP_PENALTY', 'INTEL_CEP_MULT', 'DESTRUCTION_THRESHOLD',
            'CASUALTY_FRACTION', 'HOSPITAL_CASUALTY_REDUCTION', 'RESOLVE_MERGE',
        ] },
    { id: 'intel', title: 'Intelligence', names: ['INTEL_DECAY_PER_ROUND', 'INTEL_DECAY_INTERVAL', 'RADAR_LEAK_BANDS', 'INCOMING_INTEL'] },
    { id: 'population', title: 'Population', names: [
            'START_POPULATION', 'URBAN_HP_PER_KPOP', 'START_FEAR', 'START_UNREST', 'START_LOYALTY',
            'FEAR_PER_CASUALTY', 'FEAR_PER_LOST_UTILITY', 'FEAR_DECAY', 'UNREST_RATE', 'UNREST_RECOVERY',
            'LOYALTY_BASELINE', 'REPRESSION_PER_LOYALTY', 'REPRESSION_DECAY', 'FEAR_PER_RUIN', 'FEAR_FREEZE_AT',
            'FEAR_FREEZE_ROUNDS', 'LOYALTY_PER_CASUALTY', 'LOYALTY_PER_RUIN', 'LOYALTY_PER_REPRESSION',
            'UPRISING_LOYALTY', 'TAKEOVER_LOYALTY_CEILING', 'NUCLEAR_LAUNCH_LOYALTY', 'NUCLEAR_LAUNCH_UNREST', 'NUCLEAR_HIT_LOYALTY', 'HOME_REPAIR',
        ] },
    { id: 'salvage', title: 'Salvage and battlefield finds', names: [
            'DISCOVERY_CHANCE_HIT', 'DISCOVERY_CHANCE_MISS', 'NUCLEAR_DISCOVERY_START_ROUND', 'DISCOVERY_STASH_MIN', 'DISCOVERY_STASH_MAX',
            'SALVAGE_CHANCE', 'SALVAGE_MATERIAL_FRACTION', 'SALVAGE_DIVERT_MULT', 'SALVAGE_INTERCEPT_MULT',
        ] },
    { id: 'strategic', title: 'Strategic layer', names: [
            'BARRACKS_MITIGATION', 'DEFENCE_ESTABLISHMENT', 'DEFENCE_ESTABLISHMENT_BUFFER', 'ELASTIC_ESTABLISHMENT_PER_MISSING_CITY',
            'ESTABLISHMENT_CUT_PER_CITY_AHEAD', 'ESTABLISHMENT_MIN', 'SHELTER_ALLOWANCE',
            'BASE_SITING_CAP', 'FALLEN_DEFENCE_SHARE', 'SURRENDER_SHARE', 'TRANSFER_UNIT_SLOTS', 'TRANSFER_WARHEAD_SLOTS',
        ] },
    { id: 'development', title: 'Research', names: ['DEVELOPMENT_SPECS', 'DEVELOPMENT_EFFECTS'] },
    { id: 'buildings', title: 'Buildings', names: ['ASSET_SPECS'] },
];
/** Rules that are a choice rather than a number. */
export const TUNING_CHOICES = {
    RESOLVE_MERGE: ['arrival', 'interleave'],
};
/**
 * Numbers that look tunable and are not.
 *
 * Weapon ranges are what the pocket layout was solved against
 * (scripts/solve-pockets.mjs): change one and the guarantee of one, two and
 * four enemies in reach silently breaks. Two development effects are copies of
 * rules tuned elsewhere and follow them.
 */
const FIXED = [
    /^WEAPON_SPECS\.[^.]+\.rangeKm$/,
    /^DEVELOPMENT_EFFECTS\.(attack_2_bonus|command_2_ap)$/,
];
/**
 * Numbers the rules divide by or raise a curve to, and what each has to be.
 * A finite value can still put NaN or Infinity into the game through one of
 * these, and a NaN spreads: a city whose health is NaN never falls.
 */
const DIVISORS = [
    // A production order counts the lines it used as its units over the batch
    // (plan.ts), and a fraction of a unit is not built.
    { path: /^(PRODUCTION_BATCH\.[^.]+|DEFENCE_BATCH)$/, ok: (n) => Number.isInteger(n) && n >= 1,
        want: 'a whole number of units a line, 1 or more' },
    // What fits in the stores is the room over a weapon's slots (stockpile.ts).
    { path: /^WEAPON_SPECS\.[^.]+\.slots$/, ok: (n) => n > 0, want: 'launch slots, more than 0' },
    // The stores hold this many waves of the launch slots, and a wave of them
    // is the stores over it (the NPCs' status cards, telemetry's store use).
    { path: /^(STOCKPILE_WAVES|AIRBASE_SLOTS|SILO_SLOTS)$/, ok: (n) => Number.isInteger(n) && n >= 1, want: 'a whole number, 1 or more' },
    // Chemical exposure fades across the weapon's and warhead's splash
    // together, and one tile more (resolve.ts).
    { path: /^(WEAPON_SPECS|WARHEAD_SPECS)\.[^.]+\.splash$/, ok: (n) => n >= 0, want: 'a radius in tiles, 0 or more' },
    // A conversion is priced by one resource's worth over the other's (conversionQuote).
    { path: /^CONVERSION\.value\.[^.]+$/, ok: (n) => n > 0, want: 'a worth more than 0' },
    // Casualties are the share of a building's hit points a blast takes (resolve.ts).
    { path: /^ASSET_SPECS\.[^.]+\.hp$/, ok: (n) => n > 0, want: 'hit points, more than 0' },
    // At 0 the damage curve leaves a city at full health with the capability
    // floor, and below 0 it runs to minus infinity (resilience).
    { path: /^RESILIENCE_EXP$/, ok: (n) => n > 0, want: 'an exponent more than 0' },
];
export class TuningError extends Error {
    problems;
    constructor(problems) {
        super(`tuning refused:\n  ${problems.join('\n  ')}`);
        this.problems = problems;
    }
}
const exported = C;
const SCALARS = new Set(C.TUNABLE_SCALARS);
const SECTION_OF = new Map(TUNING_SECTIONS.flatMap((s) => s.names.map((n) => [n, s.id])));
const isNumbers = (v) => Array.isArray(v) && v.every((n) => typeof n === 'number');
const isTree = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
/** A table's tunable numbers, as a tree. Labels, lists of names and fixed values are left out. */
function numbersOf(v, path) {
    const out = {};
    for (const [k, x] of Object.entries(v)) {
        const p = `${path}.${k}`;
        if (FIXED.some((re) => re.test(p)))
            continue;
        if (typeof x === 'number')
            out[k] = x;
        else if (isNumbers(x))
            out[k] = [...x];
        else if (isTree(x)) {
            const t = numbersOf(x, p);
            if (Object.keys(t).length)
                out[k] = t;
        }
    }
    return out;
}
/** Every tunable value as the rules have it now, by constant name. */
export function currentTuning() {
    const out = {};
    for (const s of TUNING_SECTIONS) {
        for (const name of s.names) {
            const v = exported[name];
            out[name] = SCALARS.has(name) ? v : numbersOf(v, name);
        }
    }
    return out;
}
/** The code's own numbers, before anything was applied: what the lab resets to. */
export const CODE_DEFAULTS = structuredClone(currentTuning());
/** Values laid out as tuning.json has them, by section. */
export function toTuningFile(values = currentTuning()) {
    const file = {};
    for (const s of TUNING_SECTIONS) {
        file[s.id] = {};
        for (const name of s.names)
            if (name in values)
                file[s.id][name] = values[name];
    }
    return file;
}
/** tuning.json back to values by name. A name in the wrong section, or no section, is refused. */
export function fromTuningFile(file) {
    if (!isTree(file))
        throw new TuningError(['tuning.json is a JSON object of sections']);
    const problems = [];
    const out = {};
    for (const [sectionId, section] of Object.entries(file)) {
        if (!TUNING_SECTIONS.some((s) => s.id === sectionId)) {
            problems.push(`${sectionId}: no such section (${TUNING_SECTIONS.map((s) => s.id).join(', ')})`);
            continue;
        }
        if (!isTree(section)) {
            problems.push(`${sectionId}: a section is an object of values`);
            continue;
        }
        for (const [name, v] of Object.entries(section)) {
            const home = SECTION_OF.get(name);
            if (!home)
                problems.push(`${sectionId}.${name}: not a tunable value`);
            else if (home !== sectionId)
                problems.push(`${sectionId}.${name}: belongs in the ${home} section`);
            else
                out[name] = v;
        }
    }
    if (problems.length)
        throw new TuningError(problems);
    return out;
}
/** Every leaf, by dotted path: `WEAPON_SPECS.cruise.arrival` → 4. */
export function tuningLeaves(values) {
    const out = new Map();
    const walk = (v, path) => {
        if (typeof v === 'number' || typeof v === 'string' || isNumbers(v))
            out.set(path, v);
        else if (isTree(v))
            for (const [k, x] of Object.entries(v))
                walk(x, `${path}.${k}`);
    };
    for (const [name, v] of Object.entries(values))
        walk(v, name);
    return out;
}
/** The paths where two sets of values differ, with both values. */
export function tuningDiff(from, to) {
    const a = tuningLeaves(from);
    const out = [];
    for (const [path, v] of tuningLeaves(to)) {
        const was = a.get(path);
        if (JSON.stringify(was) !== JSON.stringify(v))
            out.push({ path, from: was, to: v });
    }
    return out;
}
/**
 * Check a set of values against the rules as they stand. Values may be
 * partial: a table needs only the fields being changed.
 */
export function checkTuning(values) {
    if (!isTree(values))
        return ['tuning is an object of values by name'];
    const problems = [];
    const leaf = (path, want, got) => {
        if (FIXED.some((re) => re.test(path))) {
            problems.push(`${path}: fixed (see tuning.ts)`);
            return;
        }
        if (typeof want === 'number') {
            if (typeof got !== 'number' || !Number.isFinite(got))
                problems.push(`${path}: a number, not ${JSON.stringify(got)}`);
            else if (path.startsWith('INCOMING_INTEL.')) {
                if (path.startsWith('INCOMING_INTEL.ballistic.') && /\.(bandWidth|uncertainty)$/.test(path)) {
                    if (!Number.isInteger(got) || got < (path.endsWith('bandWidth') ? 1 : 0) || got > 20)
                        problems.push(`${path}: a whole launch count ${path.endsWith('bandWidth') ? '1' : '0'}–20`);
                }
                else if (got < 0 || got > 1)
                    problems.push(`${path}: a fraction 0–1`);
            }
            else if (path.startsWith('HOLD_BACK.') && (got < 0 || got > 1)) {
                problems.push(`${path}: a share of the wave's fire, 0–1`);
            }
            else if (/^WEAPON_SPECS\.[^.]+\.arrival$/.test(path) && (!Number.isInteger(got) || got < 1)) {
                problems.push(`${path}: a whole place in the wave, 1 or more`);
            }
            else {
                const divisor = DIVISORS.find((d) => d.path.test(path));
                if (divisor && !divisor.ok(got))
                    problems.push(`${path}: ${divisor.want}`);
            }
        }
        else if (isNumbers(want)) {
            if (!isNumbers(got) || got.length !== want.length || !got.every(Number.isFinite)) {
                problems.push(`${path}: a list of ${want.length} numbers`);
            }
        }
        else if (isTree(want)) {
            if (!isTree(got)) {
                problems.push(`${path}: an object`);
                return;
            }
            for (const [k, x] of Object.entries(got)) {
                if (!(k in want))
                    problems.push(`${path}.${k}: no such field`);
                else
                    leaf(`${path}.${k}`, want[k], x);
            }
        }
        else {
            problems.push(`${path}: not tunable`);
        }
    };
    for (const [name, v] of Object.entries(values)) {
        if (!SECTION_OF.has(name)) {
            problems.push(`${name}: not a tunable value`);
            continue;
        }
        const choices = TUNING_CHOICES[name];
        if (choices) {
            if (typeof v !== 'string' || !choices.includes(v))
                problems.push(`${name}: one of ${choices.join(', ')}`);
            continue;
        }
        leaf(name, exported[name], v);
        if (name === 'INCOMING_INTEL' && isTree(v) && isTree(v.cruise)) {
            const cruise = { ...C.INCOMING_INTEL.cruise, ...v.cruise };
            if (Number(cruise.mediumAt) >= Number(cruise.largeAt))
                problems.push('INCOMING_INTEL.cruise: mediumAt must be below largeAt');
        }
        if (name === 'DESTRUCTION_THRESHOLD' && typeof v === 'number' && (v < 0 || v >= 1)) {
            problems.push('DESTRUCTION_THRESHOLD: a fraction from 0 up to, but not including, 1');
        }
        // A place in the establishment is a whole launcher.
        for (const places of ['ELASTIC_ESTABLISHMENT_PER_MISSING_CITY', 'ESTABLISHMENT_CUT_PER_CITY_AHEAD', 'ESTABLISHMENT_MIN']) {
            if (name === places && typeof v === 'number' && (!Number.isInteger(v) || v < 0)) {
                problems.push(`${places}: a whole number of launchers, 0 or more`);
            }
        }
    }
    // Residents' will to rebuild climbs from the repair floor to the loyalty
    // baseline (repairWill), and divides by the gap between them: with none, a
    // city at the floor mends its homes to NaN, and with the floor above the
    // baseline the will runs backwards. Either may be changed alone, so each is
    // checked against the other as it will stand.
    const repair = isTree(values.HOME_REPAIR) ? values.HOME_REPAIR : {};
    if ('LOYALTY_BASELINE' in values || 'loyaltyFloor' in repair) {
        const baseline = typeof values.LOYALTY_BASELINE === 'number' ? values.LOYALTY_BASELINE : C.LOYALTY_BASELINE;
        const floor = typeof repair.loyaltyFloor === 'number' ? repair.loyaltyFloor : C.HOME_REPAIR.loyaltyFloor;
        if (!(floor < baseline))
            problems.push(`HOME_REPAIR.loyaltyFloor: must be below LOYALTY_BASELINE (floor ${floor}, baseline ${baseline})`);
    }
    return problems;
}
/**
 * Apply values to the rules, all or nothing, and return what changed. Every
 * module that reads a rule sees the new value at once: tables change in place,
 * and single-number rules are live module bindings.
 */
export function applyTuning(values) {
    const problems = checkTuning(values);
    if (problems.length)
        throw new TuningError(problems);
    const before = currentTuning();
    const assign = (target, v) => {
        for (const [k, x] of Object.entries(v)) {
            if (isTree(x))
                assign(target[k], x);
            else if (isNumbers(x))
                target[k].splice(0, x.length, ...x);
            else
                target[k] = x;
        }
    };
    for (const [name, v] of Object.entries(values)) {
        if (SCALARS.has(name))
            C.setTunable(name, v);
        else
            assign(exported[name], v);
    }
    // Copies of rules tuned elsewhere follow them.
    C.DEVELOPMENT_EFFECTS.attack_2_bonus = C.WAVE_DOCTRINE_BONUS;
    C.DEVELOPMENT_EFFECTS.command_2_ap = C.STAFF_ACTION_POINTS;
    return tuningDiff(before, currentTuning()).map((d) => d.path);
}
/** Put every rule back to the code's own numbers. */
export function resetTuning() {
    applyTuning(CODE_DEFAULTS);
}
//# sourceMappingURL=tuning.js.map